import Luminous from 'luminous-lightbox';
import LuminousGallery from 'luminous-lightbox-gallery';
import {Tooltip} from 'bootstrap';
import 'bootstrap';
import FormHelpers from './components/form-helpers';
import GeneralUtility from './utilities/general-utility';
import ConsentManager from './extensions/consent-manager';
import JoiValidation from './components/joi-validation';
import ChecklistPage from './components/checklist-page';
import ManageHotelOffer from './plugins/manage-offer/manage-hotel-offer/manage-hotel-offer';
import ManageLiftOffer from './plugins/manage-offer/manage-lift-offer/manage-lift-offer';
import ManageSkischoolOffer from './plugins/manage-offer/manage-skischool-offer/manage-skischool-offer';
import ManageEquipmentOffer from './plugins/manage-offer/manage-equipment-offer/manage-equipment-offer';
import ManagePackageOffer from './plugins/manage-offer/manage-package-offer/manage-package-offer';
import ManageLocalTransportOffer from './plugins/manage-offer/manage-local-transport-offer/manage-local-transport-offer';
import ManageExternalTransportOffer from './plugins/manage-offer/manage-external-transport-offer/manage-external-transport-offer';
import OfferOverview from './plugins/manage-offer/offer-overview/offer-overview';
import ChecklistStep from './plugins/checklist/checklist-step/checklist-step';
import ManageTeacherUser from './plugins/manage-user/manage-teacher-user/manage-teacher-user';
import ManageVendorUser from './plugins/manage-user/manage-vendor-user/manage-vendor-user';
import TeacherRegistration from './plugins/registration/teacher-registration/teacher-registration';
import VendorRegistration from './plugins/registration/vendor-registration/vendor-registration';
import HotelOffer from './plugins/offer/hotel-offer/hotel-offer';
import LiftOffer from './plugins/offer/lift-offer/lift-offer';
import SkischoolOffer from './plugins/offer/skischool-offer/skischool-offer';
import EquipmentOffer from './plugins/offer/equipment-offer/equipment-offer';
import PackageOffer from './plugins/offer/package-offer/package-offer';
import LocalTransportOffer from './plugins/offer/local-transport-offer/local-transport-offer';
import ExternalTransportOffer from './plugins/offer/external-transport-offer/external-transport-offer';
import Checklist from './plugins/checklist/checklist';
import ScrollPosition from '../types/scroll-position';
import WindowPosition from '../types/window-position';
import ManageSportOffer from './plugins/manage-offer/manage-sport-offer/manage-sport-offer';
import SportOffer from './plugins/offer/sport-offer/sport-offer';
import LanguageUtility from './utilities/language-utility';
import StudentRegistration from './plugins/student-registration/student-registration';

/**
 * Main entry point for the website.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class Main {
    /**
     * Top-offset for scrollspy-anchors.
     */
    private scrollSpyOffset: number = -100;

    constructor() {
        this.initializeMainNavigation();
        this.initializeOffcanvasNavigation();
        this.initializeTouch();
        this.initializeLuminous();
        this.initializeTooltips();
        this.initializeModals();
        this.initializePlugins();
        this.initializeExtensions();
        this.initializeContent();
        this.initializeScrollspy();
        this.initializeCopy();

        // Initialize the checklist pages
        const sectionChecklist = document.getElementById('section-checklist');
        const checklistMenu = document.getElementById('checklistMenu');
        if (sectionChecklist && checklistMenu) {
            new ChecklistPage(sectionChecklist, checklistMenu);
        }

        // Setup form helpers
        new FormHelpers();

        // Initialize Joi-Form-validiation for forms using certain data attributes
        JoiValidation.initializeDataAttributeValidation();

        // Listen to scrolling
        this.fixViewHeight();
        GeneralUtility.browserEmitter.on('scroll', scrollPosition => {
            this.fixViewHeight();
            this.adjustMainmenuOnScrolling(scrollPosition);
        });

        // Listen to device orientation
        this.adjustDeviceOrientation(GeneralUtility.getDeviceOrientation());
        GeneralUtility.browserEmitter.on('orientation', orientation => {
            this.fixViewHeight();
            this.adjustDeviceOrientation(orientation);
        });
    }

    /**
     * Initializes the main navigation.
     */
    private initializeMainNavigation(): void {
        const offcanvasNavbar = document.getElementById('offcanvasNavbar');
        if (!offcanvasNavbar) {
            return;
        }

        offcanvasNavbar.addEventListener('shown.bs.offcanvas', () => {
            document.body.classList.add('offcanvas-open');
        });
        offcanvasNavbar.addEventListener('hidden.bs.offcanvas', () => {
            document.body.classList.remove('offcanvas-open');
        });
    }

    /**
     * Initialize touch functionality.
     */
    private initializeTouch() {
        if (GeneralUtility.isTouch()) {
            document.body.classList.add('is-touch');
            document.body.classList.remove('no-touch');
        } else {
            document.body.classList.add('no-touch');
            document.body.classList.remove('is-touch');
        }
    }

    /**
     * Adjusts the mainmenu when scrolling.
     *
     * @param {ScrollPosition} position Previous window.pageYOffset value
     */
    private adjustMainmenuOnScrolling(position: ScrollPosition) {
        const header = document.querySelector('body > header');
        if (!header) {
            return;
        }

        const headerHeight = header.clientHeight;
        const offset = window.innerHeight / 2;

        // Scrolling up
        if (position.previousScrollY > position.scrollY) {
            // Show header
            header.classList.add('header-out', 'header-in');

            // Remove header background color when scrolling near the top
            if (window.scrollY < offset) {
                header.classList.remove('header-fixed-bg');
            }

            // Reset classes when scrolled to top
            if (window.scrollY === 0) {
                header.classList.remove('header-fixed', 'header-out', 'header-in');
            }
        }

        // Scrolling down
        else {
            // Hide header when user scrolled passed it
            if (position.scrollY > headerHeight) {
                header.classList.add('header-fixed', 'header-fixed-bg');
                header.classList.remove('header-in');
            }
        }
    }

    /**
     * Adjusts the orientation.
     *
     * @param {string} orientation Device orientation
     */
    private adjustDeviceOrientation(orientation: string): void {
        if (orientation === 'landscape') {
            document.body.classList.add('landscape');
            document.body.classList.remove('portrait');
        } else {
            document.body.classList.remove('landscape');
            document.body.classList.add('portrait');
        }
    }

    /**
     * Fixes a bug where mobile devices like iPad calculate viewheight-property wrongly.
     */
    private fixViewHeight(): void {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', vh + 'px');
    }

    /**
     * Initializes the inline navigation if enabled and items are available. The Scrollspy logic is implement manually, as the Bootstrap variant will not
     * work properly with simple anchors, that do not wrap a whole section. See here: https://getbootstrap.com/docs/5.2/components/scrollspy/#simple-anchors.
     * If content between two anchors is large, then when scrolling past an anchor, the active state will be lost until the next anchor. The custom
     * implementation here makes sure, that the active state is kept, until the window scrolls past some other anchor.
     */
    private initializeScrollspy(): void {
        const sections = document.querySelectorAll<HTMLElement>('.section[data-scrollspy-section="1"]');
        const scrollSpyNavigation = document.querySelector<HTMLElement>('#scrollspy-navigation');
        const mobileScrollspyNavigation = document.querySelector<HTMLElement>('#mobile-scrollspy-navigation');
        if (!sections || sections.length === 0 || !scrollSpyNavigation || !mobileScrollspyNavigation) {
            return;
        }
        const navigationList = scrollSpyNavigation.querySelector<HTMLUListElement>('ul');
        const mobileNavigationList = mobileScrollspyNavigation.querySelector<HTMLUListElement>('ul');
        const mobileButton = mobileScrollspyNavigation.querySelector<HTMLButtonElement>('button');
        if (!navigationList || !mobileNavigationList || !mobileButton) {
            return;
        }
        const mobileReadMore = mobileButton.querySelector<HTMLElement>('.read-more');
        if (!mobileReadMore) {
            return;
        }

        // Class for defining the currently active section
        class ActiveSection {
            index: number = 0;
            section: HTMLElement | null = null;
        }

        /**
         * Calculate where each section is within the window.
         *
         * @param {NodeListOf<HTMLElement>} sections Sections on the page
         * @returns {WindowPosition[]} Position for each section
         */
        const calculateSectionPositions = (sections: NodeListOf<HTMLElement>): WindowPosition[] => {
            const positions: WindowPosition[] = [];
            sections.forEach(section => {
                positions.push(GeneralUtility.getElementPosition(section));
            });
            return positions;
        };

        /**
         * Finds the last possible section based on the current scroll position
         *
         * @param {ScrollPosition} scrollPosition Current scroll position
         * @param {WindowPosition[]] }sectionPositions Positions of all sections
         * @param {number} [offset] Offset while comparing the current scroll position with the section position
         * @returns {ActiveSection}
         */
        const findActiveSection = (scrollPosition: ScrollPosition, sectionPositions: WindowPosition[], offset: number = 0): ActiveSection => {
            const activeSection = new ActiveSection();
            for (let i = 0; i < sectionPositions.length; i++) {
                const position = sectionPositions[i];
                if (scrollPosition.scrollY > position.top + offset) {
                    activeSection.index = i;
                    activeSection.section = sections.item(i);
                }
            }
            return activeSection;
        };

        /**
         * Adds the offset class to all section anchors.
         *
         * @param {NodeListOf<HTMLElement>} sections Sections
         * @returns {void}
         */
        const addAnchorOffset = (sections: NodeListOf<HTMLElement>) => {
            sections.forEach(section => {
                section.classList.add('scollspy-section-offset');
            });
        };

        /**
         * Removes the offset class from all section anchors.
         *
         * @param {NodeListOf<HTMLElement>} sections Sections
         * @returns {void}
         */
        const removeAnchorOffset = (sections: NodeListOf<HTMLElement>) => {
            sections.forEach(section => {
                section.classList.remove('scollspy-section-offset');
            });
        };

        // Add navigation elements based on the current content element sections to the lists
        sections.forEach(function (section: HTMLElement) {
            navigationList.innerHTML += `
<li class="nav-item">
    <a class="nav-link" href="#${section.id}" data-section="${section.id}">${section.dataset.title}</a>
</li>
`;
            mobileNavigationList.innerHTML += `
<li>
    <a class="dropdown-item" href="#${section.id}" data-section="${section.id}">${section.dataset.title}</a>
</li>
`;
        });

        // Fetch just created navigation items
        const navigationListItems = navigationList.querySelectorAll<HTMLElement>('.nav-link');
        const mobileNavigationListItems = mobileNavigationList.querySelectorAll<HTMLElement>('.dropdown-item');
        if (!navigationListItems || !mobileNavigationListItems) {
            return;
        }

        // Set active states based on the current scroll position; keep elements active, until scrolling past another anchor
        let activeSection: ActiveSection | null = null;
        let previousActiveSection: ActiveSection | null = null;
        GeneralUtility.browserEmitter.on('scroll', scrollPosition => {
            const sectionPositions = calculateSectionPositions(sections);
            activeSection = findActiveSection(scrollPosition, sectionPositions, this.scrollSpyOffset);

            // Section still the same; no change necessary
            if (activeSection && previousActiveSection && activeSection.section === previousActiveSection.section) {
                return;
            }

            // Adjust desktop navigation
            navigationListItems.forEach(navItem => {
                if (
                    activeSection &&
                    activeSection.section &&
                    navItem.dataset.section === activeSection.section.getAttribute('id')
                ) {
                    navItem.classList.add('active');
                } else {
                    navItem.classList.remove('active');
                }
            });

            // Adjust mobile navigation
            if (activeSection && activeSection.section && activeSection.section.dataset.title) {
                mobileReadMore.innerHTML = activeSection.section.dataset.title;
            } else if (mobileButton.dataset.initialText) {
                mobileReadMore.innerHTML = mobileButton.dataset.initialText;
            }

            mobileNavigationListItems.forEach(navItem => {
                if (
                    activeSection &&
                    activeSection.section &&
                    navItem.dataset.section === activeSection.section.getAttribute('id')
                ) {
                    navItem.classList.add('bg-light-steel-blue-30');
                } else {
                    navItem.classList.remove('bg-light-steel-blue-30');
                }
            });

            previousActiveSection = activeSection;
        });

        // If clicking through the navigation items from bottom to top, the header is sliding down (at least 90% of the time, depending on the browser). In that
        // case, add the offset-class to all anchors, so that the targeted section will not be hidden by the now visible header.
        // When clicking through the navigation items from top to bottom, this offset can be removed, as the header is not shown when scrolling downwards.
        navigationListItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                if (activeSection && index < activeSection.index) {
                    addAnchorOffset(sections);
                } else {
                    removeAnchorOffset(sections);
                }
            });
        });

        mobileNavigationListItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                if (activeSection && index < activeSection.index) {
                    addAnchorOffset(sections);
                } else {
                    removeAnchorOffset(sections);
                }
            });
        });
    }

    /**
     * Initializes the copy function based on DOM triggers.
     */
    private initializeCopy(): void {
        document.querySelectorAll<HTMLElement>('[data-copy]').forEach(trigger => {
            trigger.addEventListener('click', () => {
                if (!trigger.dataset.copy) {
                    return;
                }
                const target = document.querySelector<HTMLElement>(trigger.dataset.copy);
                if (!target || !target.textContent || !target.textContent.trim()) {
                    return;
                }
                GeneralUtility.copyToClipboard(target.textContent.trim());

                const triggerText = trigger.querySelector<HTMLElement>('.text');
                if (triggerText) {
                    const originalText = triggerText.textContent;
                    triggerText.textContent = LanguageUtility.getLabel('copied');
                    setTimeout(() => triggerText.textContent = originalText, 1000);
                }
            });
        });
    }

    /**
     * Initializes any offcanvas navigations; mainly used for the checklist pages.
     */
    private initializeOffcanvasNavigation(): void {
        const checklistOffcanvas = document.querySelector<HTMLElement>('#checklistOffcanvas');
        if (!checklistOffcanvas) {
            return;
        }

        checklistOffcanvas.addEventListener('show.bs.collapse', () => {
            document.body.classList.add('checklistoffcanvas-open');
        });
        checklistOffcanvas.addEventListener('hide.bs.collapse', () => {
            document.body.classList.remove('checklistoffcanvas-open');
        });
    }

    /**
     * Initializes the luminous module.
     */
    private initializeLuminous(): void {
        const links = document.body.querySelectorAll<HTMLElement>('a.lightbox');
        const groups: { [key: string]: [Element?] } = {};

        links.forEach(link => {
            const rel = link.getAttribute('rel');
            if (!rel) {
                return;
            }

            // Group links into same rel attributes
            if (!groups.hasOwnProperty(rel)) {
                groups[rel] = [];
            }
            groups[rel].push(link);
        });

        const lightboxOptions: any = {
            /**
             * Returns the caption for an element.
             *
             * @param {HTMLElement} trigger Element that triggered the lightbox.
             * @returns {string} Caption for the element
             */
            'caption': ((trigger: HTMLElement) => {
                const figure = trigger.parentElement;
                if (!figure || !figure.nextSibling) {
                    return '';
                }

                const after = figure.nextSibling as HTMLElement;
                if (after.innerText) {
                    return after.innerText;
                }

                return '';
            })
        };

        const galleryOptions: any = {
            'arrowNavigation': true
        };

        // Initialize simple lightbox or gallery for the groups
        for (const rel of Object.keys(groups)) {
            const group = groups[rel];

            if (group.length === 1) {
                new Luminous(group[0], lightboxOptions);
            } else {
                new LuminousGallery(group, galleryOptions, lightboxOptions);
            }
        }
    }

    /**
     * Initializes bootstrap tooltips.
     */
    private initializeTooltips(): void {
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(tooltip => {
            new Tooltip(tooltip);
        });
    }

    /**
     * Initializes bootstrap modals.
     */
    private initializeModals(): void {
        // Hide all open tooltips when a modal opens, to prevent z-index errors
        document.querySelectorAll<HTMLElement>('.modal').forEach(modal => {
            modal.addEventListener('show.bs.modal', () => {
                GeneralUtility.hideAllTooltips();
            });
        });
    }

    /**
     * Initializes all plugins.
     */
    private initializePlugins(): void {
        // Plugins for the offer management
        new ManageHotelOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageHotelOffer"]'));
        new ManageLiftOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageLiftOffer"]'));
        new ManageSkischoolOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageSkischoolOffer"]'));
        new ManageEquipmentOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageEquipmentOffer"]'));
        new ManagePackageOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="managePackageOffer"]'));
        new ManageLocalTransportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageLocalTransportOffer"]'));
        new ManageExternalTransportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageExternalTransportOffer"]'));
        new ManageSportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageSportOffer"]'));
        new OfferOverview(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="offerOverview"]'));

        // Plugins for the checklist
        new Checklist(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="checklist"]'));
        new ChecklistStep(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="checklistStep"]'));

        // Plugins for managing frontend users
        new ManageTeacherUser(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageTeacherUser"]'));
        new ManageVendorUser(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="manageVendorUser"]'));

        // Plugins for the user registration
        new TeacherRegistration(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="teacherRegistration"]'));
        new VendorRegistration(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="vendorRegistration"]'));

        // Plugins for showing offers in general lists, detail views and such
        new HotelOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="hotelOffer"]'));
        new LiftOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="liftOffer"]'));
        new SkischoolOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="skischoolOffer"]'));
        new EquipmentOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="equipmentOffer"]'));
        new PackageOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="packageOffer"]'));
        new LocalTransportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="localTransportOffer"]'));
        new ExternalTransportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="externalTransportOffer"]'));
        new SportOffer(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="sportOffer"]'));

        // Other plugins
        new StudentRegistration(document.querySelectorAll('[data-extension="sitepackage"][data-plugin="studentRegistration"]'));
    }

    /**
     * Initializes other TYPO3 extensions.
     */
    private initializeExtensions(): void {
        document.querySelectorAll<HTMLElement>('[data-extension="consent_manager"]').forEach(container => {
            new ConsentManager(container);
        });
    }

    /**
     * Initializes all content elements.
     */
    private initializeContent(): void {
        // Initialize custom content elements if needed. Extend from abstract class "content".
        // document.querySelectorAll('[data-ctype="textmedia"]').forEach((container: HTMLElement) => {
        //     new TextMedia(container);
        // });
    }
}
